import { Can } from '@casl/react';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import { SectionBlock } from '../../../blocks/SectionBlock';
import { Button } from '../../../common/Button';
import { SectionHeader } from '../../../common/SectionHeader';
import { Spinner } from '../../../components/Spinner';
import { useAbility } from '../../../contexts/AbilityContext/AbilityProvider';

import { TeamSettingsForm } from './components/TeamSettingsForm';
import { useTeamSettings } from './hooks/useTeamSettings';

function ActionButton({
  handleOnClick,
  children,
  disabled,
}: {
  handleOnClick: () => void | Promise<void>;
  children: React.ReactNode;
  disabled: boolean;
}): JSX.Element {
  return (
    <Button
      className={`whitespace-nowrap p-0 bg-[#CE3F4F] text-white ${
        disabled ? 'bg-gray-500 opacity-50 cursor-not-allowed' : ''
      }`}
      size="sm"
      disabled={disabled}
      onClick={() => {
        const result = handleOnClick();
        if (result instanceof Promise) {
          result.catch(console.error);
        }
      }}
    >
      <div className="flex items-center gap-2 min-w-fit font-nunito text-sm font-semibold leading-6 text-white">
        {children}
      </div>
    </Button>
  );
}

function SaveButton({
  handleOnClick,
  disabled,
}: {
  handleOnClick: () => Promise<void>;
  disabled: boolean;
}): JSX.Element {
  return (
    <ActionButton handleOnClick={handleOnClick} disabled={disabled}>
      Save settings
    </ActionButton>
  );
}

function EditButton({
  handleOnClick,
  disabled,
}: {
  handleOnClick: () => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <ActionButton handleOnClick={handleOnClick} disabled={disabled}>
      <AdjustmentsHorizontalIcon className="h-4 w-4" />
      Edit team settings
    </ActionButton>
  );
}

export function TeamSettings(): JSX.Element {
  const ability = useAbility();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    form,
    isEditOn,
    setIsEditOn,
    userOptions,
    teamOptions,
    isLoadingTeams,
    isLoadingDetails,
    isLoadingUsers,
    handleSave,
  } = useTeamSettings();

  if (isLoadingTeams || isLoadingDetails || isLoadingUsers) {
    return (
      <div className="flex justify-center items-center w-full h-full">
        <Spinner />
      </div>
    );
  }

  const handleEditClick = (): void => {
    setIsEditOn(true);
  };

  const handleSaveClick = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      await handleSave();
      setIsEditOn(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full h-full">
      <SectionHeader
        title="Team Settings"
        rightSection={
          <Can I="update" a="Team" ability={ability}>
            {isEditOn ? (
              <SaveButton
                handleOnClick={handleSaveClick}
                disabled={isSubmitting}
              />
            ) : (
              <EditButton
                handleOnClick={handleEditClick}
                disabled={isSubmitting}
              />
            )}
          </Can>
        }
      />
      <SectionBlock skipContentContainer>
        <TeamSettingsForm
          form={form}
          isEditOn={isEditOn}
          userOptions={userOptions}
          teamOptions={teamOptions}
          setIsEditOn={setIsEditOn}
          handleSave={handleSave}
        />
      </SectionBlock>
    </div>
  );
}
